import hseExpandedLogo from "../assets/images/hse-expanded-logo.png";
import pmpLogoName from "../assets/images/pmp-logo-name.png";
import { isUat } from "../utils/environment";

// The auth flow (login, forgot/set password, MFA, OAuth callbacks) renders
// before any tenant theme is applied. HSE branding is only wanted on UAT; every
// other environment keeps the default patientMpower branding.
//
// Evaluated once at module load — REACT_APP_ENVIRONMENT is fixed for the
// lifetime of a build, so there is no need to re-check per render.
const useHseBranding = isUat();

// Logo image + alt text for the pre-login screens.
export const preLoginLogo = useHseBranding
  ? { src: hseExpandedLogo, alt: "HSE logo" }
  : { src: pmpLogoName, alt: "patientMpower logo" };

// Base sizing for the login / forgot-password / MFA / set-password logo. The
// two brand logos have different aspect ratios, so the box dimensions differ.
// Spread onto the styled Logo's base style; the "@sm" mobile sizing is shared.
export const preLoginLogoSize = useHseBranding
  ? {
      width: "422px",
      height: "89px",
      marginTop: "30px",
      marginBottom: "27px",
    }
  : {
      width: "228px",
      height: "54px",
      marginTop: "40px",
      marginBottom: "48px",
    };

// HSE primary-button palette for the pre-login screens. These render before any
// tenant theme is applied, so the global --colors-button-* default (the
// patientMpower purple) would otherwise show. Mirrors the tenant "Primary
// portal button" config so the auth flow is already on-brand.
//
// Spread onto the button wrapper's css(): the inner <button> reads these custom
// properties via var(--colors-button-*), so overriding them locally re-themes
// only that button without touching the global default theme. Empty off UAT, so
// the button keeps the patientMpower-themed default.
export const preLoginPrimaryButtonVars = useHseBranding
  ? ({
      "--colors-button-background": "#006152",
      "--colors-button-text": "#ffffff",
      "--colors-button-border": "#006152",
      "--colors-button-hover-background": "#006152",
      "--colors-button-hover-border": "#006152",
      "--colors-button-focus-border": "#d1f7ed",
      "--colors-button-active-background": "#00716c",
      // Keep the loading state on-brand too (the config only defines the 7 above).
      "--colors-button-loading-background": "#006152",
      "--colors-button-loading-border": "#006152",
      "--colors-button-loading-hover-background": "#006152",
      "--colors-button-loading-hover-border": "#006152",
    } as const)
  : ({} as const);

// "Forgot password?"-style auxiliary link buttons on the pre-login screens.
// StyledAuxiliarButton reads these with a fallback to the themed primary, so
// only pre-login usages turn HSE green (post-login usages stay themed). Empty
// off UAT, so the link falls back to the themed primary.
export const preLoginAuxLinkVars = useHseBranding
  ? ({
      "--portal-auth-link-color": "#007e6c",
      "--portal-auth-link-hover-color": "#006152",
    } as const)
  : ({} as const);

// Solid primary-button background for the create-password success and
// invalid-link pages, which hardcode a color rather than reading the button
// CSS vars. Falls back to the themed $primary-40 token off UAT.
export const preLoginPrimaryButtonColor = useHseBranding
  ? "#006152"
  : "$primary-40";
